import React from 'react';
import {NavLink} from 'react-router-dom';
import styled, {css} from 'styled-components';

import {Divider} from '../../custom-feature/divider/Divider';
import {Icon} from '../../elements/Icon/Icon';
import {AccordionBody, AccordionBtn, CardAccordion} from '../../elements/accordion-v2/Accordion.tsx';
import {useAccordionFunctions} from '../../elements/accordion-v2/useAccordionFunctions.ts';
import {Btn} from '../../elements/button/btn-type/Btn';

/**
 * @description
 * **Aside Nav**
 *
 * The `<Aside.Nav> `component is used as a navigation feature within the <Aside> component to filter the page you are using and display other content with NavLink from React.
 *
 * @param {object} param0
 *
 * @param {boolean} [param0.isDefaultExpanded]
 * Specifies whether the accordion should be expanded by default. Defaults to `false`.
 *
 * @param {React.ReactNode} [param0.subNav]
 * Content to be displayed as sub-navigation. This can include any valid React element or component.(View for example below!)
 *
 * @param {import("react").ReactNode} [param0.children]
 * The children of the component.
 *
 * @param {string} [param0.to]
 * The destination.
 *
 * @example
 * ```js
 * // === Aside navigation setup === //
 *
 * import {Switch, Route, NavLink, useRouteMatch} from 'react-router-dom';
 * export const YourComponent = () => {
 *     const {url, path} = useRouteMatch();
 *     return (
 *         <>
 *             <TopNav>
 *                 <NavigationTopbar.Title>Table details</NavigationTopbar.Title>
 *             </TopNav>
 *             <Page>
 *                 <Aside>
 *                     // Random aside content //
 *                     <Aside.Nav to={`${url}/navOption01`}>
 *                         Nav option 1
 *                     </Aside.Nav>
 *                     <Aside.Nav to={`${url}/navOption02`}>
 *                         Nav option 2
 *                     </Aside.Nav>
 *                     <Aside.Nav to={`${url}/navOption03`}>
 *                         Nav option 3
 *                     </Aside.Nav>
 *                 </Aside>
 *                 <Main>
 *                     <Switch>
 *                         <Route exact path={path}>
 *                              <h3>his is the default path without any nested paths.</h3>
 *                         </Route>
 *                         <Route exact path={`${path}/navOption01`}>
 *                             <div>show Nav option 1</div>
 *                         </Route>
 *                         <Route exact path={`${path}/navOption02`}>
 *                             <div>show Nav option 2</div>
 *                         </Route>
 *                         <Route exact path={`${path}/navOption03`}>
 *                             <div>show Nav option 3</div>
 *                         </Route>
 *                     </Switch>
 *                 </Main>
 *             </Page>
 *         </>
 *     );
 * };
 *
 * // === Aside accordion setup === //
 *
 *  <Aside.Nav
 *      accordion={
 *          <>
 *              <Aside.Section>
 *                  // Place as many content as you need //
 *              </Aside.Section>
 *          </>
 *      }
 *      to={to}
 *      onClick={onClick}>
 *      // Parent accordion nav txt //
 * </Aside.Nav>
 * ```
 */
export const AsideNav = ({to, accordion, onClick, addTitle, exact, isDefaultExpanded = false, children}) => {
    const {isOpening, shouldRenderContent, isOpen, isClosing, toggle} = useAccordionFunctions({isDefaultExpanded});

    return (
        <>
            <Wrapper accordion={accordion}>
                {accordion && (
                    <NavAccordionBtn
                        link
                        color="primary"
                        onClick={toggle}
                        isClosing={isClosing}
                        isRenderContent={shouldRenderContent}
                        isOpening={isOpening}>
                        <Icon name="keyboardArrowRight" />
                    </NavAccordionBtn>
                )}

                {to && (
                    <NavLink to={to} exact={exact}>
                        {children}
                    </NavLink>
                )}

                {accordion && !to && (
                    <AccordionHeaderBtn link onClick={toggle}>
                        {children}
                    </AccordionHeaderBtn>
                )}

                {onClick && (
                    <AddBtn title={addTitle} color="primary" subtle onClick={onClick} link>
                        <Icon name="add" />
                    </AddBtn>
                )}
            </Wrapper>

            <Divider />

            {accordion && (
                <CardAccordion
                    isClosing={isClosing}
                    isOpening={isOpening}
                    isRenderContent={shouldRenderContent}
                    isOpen={isOpen}>
                    {(shouldRenderContent || isOpening || isOpen) && <SubNav>{accordion}</SubNav>}
                </CardAccordion>
            )}
        </>
    );
};
const AccordionHeaderBtn = styled(Btn)`
    position: relative;
    padding: var(--aside-padding) var(--aside-padding) var(--aside-padding) var(--accordion-nav-padding);
    display: block;
    color: var(--container-text-active-100);
    text-decoration: none;
    width: 100%;
    text-align: left;
    border-radius: 0;

    &:hover {
        color: var(--container-text-hover-100);
        background: var(--container-background-hover-color-400);
    }
`;
const AddBtn = styled(Btn)`
    ${Icon.Wrapper} svg {
        transition: 0.6s;
        width: 16px;
        height: 16px;
        color: var(--container-text-active-100);
    }

    &:hover {
        ${Icon.Wrapper} svg {
            transform: scale(1.1);
            color: var(--container-text-hover-100);
        }
    }
`;

const Wrapper = styled.div`
    --accordion-nav-padding: calc(var(--aside-padding) + var(--space-md) + var(--space-sm));
    position: relative;

    a {
        position: relative;
        padding: var(--aside-padding);
        display: block;
        color: var(--container-text-active-100);
        text-decoration: none;

        small {
            color: var(--container-text-active-100);
        }

        &:hover {
            color: var(--container-text-hover-100);
            background: var(--container-background-hover-color-400);

            small {
                color: var(--container-text-hover-100);
            }
        }

        &.active {
            transition: 0.6s;
            background: var(--container-background-active-color-200);

            &:hover {
                color: var(--container-text-hover-100);
                background: var(--container-background-hover-color-400);
            }
        }
    }

    ${({accordion}) => {
        if (accordion)
            return css`
                position: relative;

                a {
                    padding-left: var(--accordion-nav-padding);
                }

                ${AddBtn},
                ${NavAccordionBtn} {
                    position: absolute;
                    top: 50%;
                    transform: translateY(-50%);
                    z-index: 2;
                }

                ${AddBtn} {
                    right: var(--aside-padding);
                }

                ${NavAccordionBtn} {
                    left: var(--aside-padding);
                }
            `;
    }}
`;

const SubNav = styled(AccordionBody)`
    a {
        padding-left: var(--accordion-nav-padding);
    }
`;

const NavAccordionBtn = styled(AccordionBtn)`
    ${({isRenderContent, isOpening}) => {
        if (isRenderContent && isOpening)
            return css`
                ${Icon.Wrapper} svg {
                    transition: 0.6s;
                    transform: rotate(90deg) !important;
                }

                &:hover {
                    ${Icon.Wrapper} svg {
                        transform: rotate(90deg) scale(1.4) !important;
                    }
                }
            `;
    }}
`;
